import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import "./App.css";

const BirthdayExperience = () => {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [candleLit, setCandleLit] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const canBlowRef = useRef(false);
  const audioRef = useRef(null);
  const fireworksAudioRef = useRef(null);
  const micStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const fireworksCanvasRef = useRef(null);
  const fireworksAnimationRef = useRef(null);
  const fireworksBurstIntervalRef = useRef(null);
  const audioContextRef = useRef(null);

  // Get or create AudioContext
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Resume AudioContext (required for user gesture)
  const resumeAudioContext = useCallback(async () => {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch (e) {
        console.warn("Could not resume AudioContext:", e);
      }
    }
  }, [getAudioContext]);

  // Handle audio playback
  const startAudio = () => {
    if (audioRef.current) {
      audioRef.current.play().catch((err) => {
        console.log("Audio autoplay blocked:", err);
      });
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const newMutedState = !isMuted;
    if (audioRef.current) {
      audioRef.current.muted = newMutedState;
    }
    if (fireworksAudioRef.current) {
      fireworksAudioRef.current.muted = newMutedState;
    }
    setIsMuted(newMutedState);
  };

  // Start experience
  const handleStart = async () => {
    setStarted(true);
    await resumeAudioContext(); // Resume AudioContext for mic detection
    startAudio();
    startFireworks(); // Start fireworks when experience begins

    // Start fireworks background audio at low volume
    if (fireworksAudioRef.current) {
      fireworksAudioRef.current.volume = 0.15; // Low volume (15%)
      fireworksAudioRef.current.loop = true;
      fireworksAudioRef.current.play().catch((err) => {
        console.log("Fireworks audio autoplay blocked:", err);
      });
    }

    setTimeout(() => setStep(1), 800);
  };

  const blowOutCandle = useCallback(() => {
    setCandleLit((prev) => {
      if (!prev) return prev;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Resume and increase fireworks audio volume to 50% when candle is blown
      if (fireworksAudioRef.current) {
        fireworksAudioRef.current.volume = 0.5; // 50% volume
        fireworksAudioRef.current.play().catch((err) => {
          console.log("Fireworks audio play error:", err);
        });
      }

      setTimeout(() => setStep(6), 1500);
      return false;
    });
  }, []);

  // Candle blow detection using microphone - simplified approach like LuffyBirthday
  const setupMicDetection = useCallback(() => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      console.log("Microphone not available");
      return;
    }

    // Lower fireworks audio volume during mic detection to reduce interference
    if (fireworksAudioRef.current) {
      fireworksAudioRef.current.volume = 0.05; // Very low volume (5%)
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        micStreamRef.current = stream;
        const ctx = getAudioContext();
        const analyser = ctx.createAnalyser();
        const microphone = ctx.createMediaStreamSource(stream);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;
        microphone.connect(analyser);
        analyserRef.current = analyser;

        const detectBlow = () => {
          // Check current state using ref (avoids stale closure)
          setCandleLit((currentCandleLit) => {
            if (!canBlowRef.current || !currentCandleLit) {
              if (currentCandleLit) {
                animationFrameRef.current = requestAnimationFrame(detectBlow);
              }
              return currentCandleLit;
            }

            analyser.getByteFrequencyData(dataArray);
            const average =
              dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

            // Only allow blow after countdown finishes and canBlow is true
            // Lower threshold for better sensitivity (was 30, now 20)
            if (average > 20) {
              blowOutCandle();
              return false;
            }

            animationFrameRef.current = requestAnimationFrame(detectBlow);
            return currentCandleLit;
          });
        };

        animationFrameRef.current = requestAnimationFrame(detectBlow);
      })
      .catch((err) => {
        console.log("Microphone access denied:", err);
        // Fallback: user can click button if mic doesn't work
      });
  }, [getAudioContext, blowOutCandle]);

  // Auto-transition effects
  useEffect(() => {
    if (!started) return;

    let timeoutId;

    // Step 1: Welcome - 5 seconds
    if (step === 1) {
      timeoutId = setTimeout(() => setStep(2), 5000);
    }
    // Step 2: Card 1 - 8 seconds
    else if (step === 2) {
      timeoutId = setTimeout(() => setStep(3), 8000);
    }
    // Step 3: Leadership - 8 seconds
    else if (step === 3) {
      timeoutId = setTimeout(() => setStep(4), 8000);
    }
    // Step 4: Personal touch - 10 seconds with gradual volume fade
    else if (step === 4) {
      // Start gradual volume fade-out over 10 seconds
      if (audioRef.current) {
        const initialVolume = audioRef.current.volume;
        const fadeSteps = 20; // 20 steps over 10 seconds
        const volumeStep = initialVolume / fadeSteps;
        const fadeInterval = 10000 / fadeSteps; // Time per step

        let currentStep = 0;
        const fadeIntervalId = setInterval(() => {
          currentStep++;
          if (audioRef.current) {
            audioRef.current.volume = Math.max(
              0,
              initialVolume - volumeStep * currentStep
            );
          }
          if (currentStep >= fadeSteps) {
            clearInterval(fadeIntervalId);
            // Stop music completely
            if (audioRef.current) {
              audioRef.current.pause();
            }
          }
        }, fadeInterval);
      }

      timeoutId = setTimeout(() => {
        setCountdown(null); // Reset countdown when entering step 5
        setStep(5);
      }, 10000);
    }
    // Step 5: Candle - stop music and start countdown sequence
    else if (step === 5 && candleLit && countdown === null) {
      // Reset canBlow flag
      canBlowRef.current = false;

      // Ensure music is stopped
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.volume = 0;
      }

      // Text-to-speech: Read "Make a wish and blow up the candle"
      const utterWish = new SpeechSynthesisUtterance(
        "Make a wish and blow up the candle"
      );
      utterWish.rate = 0.9;
      utterWish.pitch = 1.1;
      utterWish.volume = 1;
      speechSynthesis.speak(utterWish);

      // Wait 5 seconds, then start countdown
      timeoutId = setTimeout(() => {
        // Text-to-speech for countdown
        const utterCountdown = new SpeechSynthesisUtterance(
          "Three, Two, One, Blow!"
        );
        utterCountdown.rate = 0.75; // Slowed down to align with visual countdown
        utterCountdown.pitch = 1.1;
        utterCountdown.volume = 1;
        speechSynthesis.speak(utterCountdown);

        setCountdown(3);
        let currentCount = 3;
        const countdownInterval = setInterval(() => {
          currentCount--;
          if (currentCount > 0) {
            setCountdown(currentCount);
          } else if (currentCount === 0) {
            setCountdown("blow");
            canBlowRef.current = true; // Enable mic detection after countdown
            clearInterval(countdownInterval);
            // Start mic detection after countdown
            setTimeout(() => {
              setupMicDetection();
            }, 500);
          }
        }, 1000);
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [step, started, candleLit, countdown, setupMicDetection, getAudioContext]);

  // Fireworks animation
  const startFireworks = useCallback(() => {
    setShowFireworks(true);

    // Use setTimeout to ensure canvas is rendered
    const initFireworks = () => {
      const canvas = fireworksCanvasRef.current;
      if (!canvas) {
        setTimeout(initFireworks, 50);
        return;
      }

      const ctx = canvas.getContext("2d");
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particles = [];
      const particleCount = 80;
      const colors = [
        "#FFD700",
        "#3B82F6",
        "#4ECDC4",
        "#6366F1",
        "#06B6D4",
        "#8B5CF6",
        "#60A5FA",
        "#A5B4FC",
        "#34D399",
        "#22D3EE",
      ];

      class Particle {
        constructor(x, y) {
          this.x = x;
          this.y = y;
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 8 + 4;
          this.vx = Math.cos(angle) * speed;
          this.vy = Math.sin(angle) * speed;
          this.life = 1;
          this.decay = Math.random() * 0.02 + 0.015;
          this.size = Math.random() * 3 + 1;
          this.color = colors[Math.floor(Math.random() * colors.length)];
        }

        update() {
          this.x += this.vx;
          this.y += this.vy;
          this.vy += 0.15; // gravity
          this.life -= this.decay;
        }

        draw() {
          ctx.save();
          ctx.globalAlpha = this.life;
          ctx.fillStyle = this.color;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      const createFirework = (x, y) => {
        for (let i = 0; i < particleCount; i++) {
          particles.push(new Particle(x, y));
        }
      };

      // Create multiple fireworks
      const createBurst = () => {
        const x = Math.random() * canvas.width;
        const y = Math.random() * (canvas.height * 0.5) + canvas.height * 0.2;
        createFirework(x, y);
      };

      // Initial burst
      setTimeout(() => {
        for (let i = 0; i < 5; i++) {
          setTimeout(() => createBurst(), i * 200);
        }
      }, 100);

      // Continue creating fireworks continuously
      fireworksBurstIntervalRef.current = setInterval(() => {
        createBurst();
      }, 400);

      let isAnimating = true;

      const animate = () => {
        if (!isAnimating) {
          return;
        }

        ctx.fillStyle = "rgba(255, 255, 255, 0.05)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        for (let i = particles.length - 1; i >= 0; i--) {
          particles[i].update();
          particles[i].draw();

          if (particles[i].life <= 0) {
            particles.splice(i, 1);
          }
        }

        if (isAnimating) {
          fireworksAnimationRef.current = requestAnimationFrame(animate);
        }
      };

      animate();

      // Keep fireworks running continuously throughout the experience
      // They will stop when component unmounts or showFireworks is set to false
    };

    initFireworks();
  }, []);

  // Fireworks are now started when experience begins and run continuously
  // No need to trigger again at step 6

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (fireworksCanvasRef.current) {
        fireworksCanvasRef.current.width = window.innerWidth;
        fireworksCanvasRef.current.height = window.innerHeight;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (fireworksAnimationRef.current) {
        cancelAnimationFrame(fireworksAnimationRef.current);
      }
      if (fireworksBurstIntervalRef.current) {
        clearInterval(fireworksBurstIntervalRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50">

      {/* Fireworks Canvas - always rendered but only visible when showFireworks is true */}
      <canvas
        ref={fireworksCanvasRef}
        className={`absolute inset-0 z-[5] pointer-events-none ${
          showFireworks ? "visible" : "hidden"
        }`}
      />

      {/* Mute Button */}
      {started && (
        <button
          onClick={toggleMute}
          className="fixed top-2 right-2 sm:top-4 sm:right-4 z-30 p-2 sm:p-3 bg-white/70 backdrop-blur-sm rounded-full shadow-lg hover:bg-white/90 active:bg-white/90 transition-all duration-300 transform hover:scale-110 active:scale-95 focus:outline-none touch-manipulation"
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
          ) : (
            <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
          )}
        </button>
      )}

      {/* Animated background shapes */}
      <div className="absolute inset-0 overflow-hidden opacity-30 z-0">
        <div className="absolute top-10 left-10 sm:top-20 sm:left-20 w-32 h-32 sm:w-64 sm:h-64 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-10 right-10 sm:bottom-20 sm:right-20 w-48 h-48 sm:w-96 sm:h-96 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 sm:w-80 sm:h-80 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Hidden audio elements */}
      <audio ref={audioRef} loop>
        <source src="/birthday-music.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={fireworksAudioRef} loop>
        <source src="/fireworks.mp3" type="audio/mpeg" />
      </audio>

      {/* Start Screen */}
      {!started && (
        <div className="relative z-10 flex flex-col items-center justify-center h-full transition-opacity duration-700 px-4">
          <button
            onClick={handleStart}
            className="group mb-6 sm:mb-8 transform transition-all duration-300 hover:scale-110 active:scale-95 focus:outline-none touch-manipulation"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-2xl group-hover:shadow-blue-300 group-active:shadow-blue-300 transition-shadow duration-300">
              <Play
                className="w-10 h-10 sm:w-12 sm:h-12 text-white ml-1"
                fill="white"
              />
            </div>
          </button>
          <p className="text-gray-600 text-base sm:text-lg font-light tracking-wide">
            Click to start
          </p>
        </div>
      )}

      {/* Main Experience */}
      {started && (
        <div className="relative z-10 flex items-center justify-center h-full p-4 sm:p-6 md:p-8">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="text-center animate-fade-in px-4">
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-2 sm:mb-4 flex flex-wrap items-center justify-center gap-1 sm:gap-2 md:gap-4">
                <span className="text-3xl sm:text-5xl md:text-6xl inline-block animate-bounce">
                  💕
                </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 font-semibold tracking-wide text-2xl sm:text-4xl md:text-5xl lg:text-6xl">
                  Happy Birthday
                </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 font-bold text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
                  Ahmed
                </span>
                <span className="text-3xl sm:text-5xl md:text-6xl inline-block animate-bounce">
                  ✨
                </span>
              </h1>
              {/* Circular photo frame */}
              <div className="flex flex-col items-center justify-center mt-6 sm:mt-8 md:mt-10">
                <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-500 rounded-full blur-xl opacity-60 animate-pulse"></div>
                  {/* Circular frame with photo */}
                  <div
                    className="relative w-full h-full rounded-full shadow-2xl overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(to bottom right, #3b82f6, #6366f1, #8b5cf6)",
                    }}
                  >
                    <div className="absolute inset-2 rounded-full overflow-hidden">
                      <img
                        src="/ahmed.jpg"
                        alt="Ahmed"
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          console.error("Failed to load image:", e);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <p className="mt-4 sm:mt-5 text-xl sm:text-2xl md:text-3xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500">
                  birthday's king 👑
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Interactive Card 1 */}
          {step === 2 && (
            <div className="text-center max-w-2xl w-full animate-fade-in px-4">
              <div className="bg-white/70 backdrop-blur-sm p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-xl transform hover:scale-105 active:scale-100 transition-all duration-500">
                <p className="text-xl sm:text-2xl md:text-3xl text-gray-700 font-light leading-relaxed">
                  Wishing you a year filled with
                  <span className="block mt-3 sm:mt-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500 font-normal">
                    endless laughter 🎉
                  </span>
                  <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500 font-normal">
                    amazing adventures ✨
                  </span>
                  <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500 font-normal">
                    and beautiful memories 🌟
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Friendship appreciation */}
          {step === 3 && (
            <div className="text-center max-w-2xl w-full animate-fade-in px-4">
              <div className="bg-white/70 backdrop-blur-sm p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-xl">
                <p className="text-lg sm:text-xl md:text-2xl text-gray-700 font-light leading-relaxed mb-4 sm:mb-6">
                  Thank you for being such
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500 font-normal">
                    a great person 💙
                  </span>
                </p>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 font-light italic">
                  Keep being the best version of yourself! ✨
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Personal touch */}
          {step === 4 && (
            <div className="text-center max-w-2xl w-full animate-fade-in px-4">
              <div className="bg-white/70 backdrop-blur-sm p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-xl transform hover:rotate-1 active:rotate-0 transition-all duration-500">
                <p className="text-xl sm:text-2xl md:text-3xl text-gray-700 font-light leading-relaxed">
                  May this year bring you
                  <br />
                  <span className="text-2xl sm:text-3xl md:text-4xl font-normal text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500">
                    lots of joy and laughter 🎈
                  </span>
                  <br />
                  <span className="text-2xl sm:text-3xl md:text-4xl font-normal text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500">
                    and all your dreams coming true 💫
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Candle */}
          {step === 5 && (
            <div className="text-center animate-fade-in px-4">
              <div className="mb-6 sm:mb-8 md:mb-12">
                <div className="relative inline-block">
                  {/* Candle */}
                  <div className="w-6 h-24 sm:w-8 sm:h-32 bg-gradient-to-b from-amber-100 to-amber-200 rounded-t-lg mx-auto border-2 border-amber-300" />

                  {/* Flame */}
                  {candleLit && (
                    <div className="absolute -top-6 sm:-top-8 left-1/2 transform -translate-x-1/2">
                      <div className="relative w-5 h-8 sm:w-6 sm:h-10 animate-flicker">
                        <div className="absolute inset-0 bg-gradient-to-t from-yellow-400 via-orange-400 to-red-400 rounded-full blur-sm" />
                        <div className="absolute inset-1 bg-gradient-to-t from-yellow-300 via-orange-300 to-red-300 rounded-full" />
                      </div>
                    </div>
                  )}

                  {/* Smoke when blown out */}
                  {!candleLit && (
                    <div className="absolute -top-10 sm:-top-12 left-1/2 transform -translate-x-1/2 animate-smoke">
                      <div className="text-3xl sm:text-4xl opacity-50">💨</div>
                    </div>
                  )}
                </div>
              </div>

              {!candleLit ? (
                <p className="text-xl sm:text-2xl text-gray-700 font-light mb-4">
                  Wish granted! ✨
                </p>
              ) : countdown === null ? (
                <p className="text-lg sm:text-xl md:text-2xl text-gray-700 font-light mb-4 px-4">
                  Make a wish and blow out the candle 🕯️
                </p>
              ) : typeof countdown === "number" ? (
                <>
                  <p className="text-6xl sm:text-7xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mb-3 sm:mb-4 animate-fade-in">
                    {countdown}
                  </p>
                  <p className="text-base sm:text-lg text-gray-600 font-light mb-6 sm:mb-8">
                    Get ready...
                  </p>
                </>
              ) : (
                <>
                  <p className="text-4xl sm:text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mb-3 sm:mb-4 animate-fade-in">
                    Blow!
                  </p>
                  <p className="text-base sm:text-lg text-gray-600 font-light mb-6 sm:mb-8">
                    Blow into your microphone
                  </p>
                </>
              )}

              {candleLit && countdown === "blow" && (
                <button
                  onClick={blowOutCandle}
                  className="px-5 py-2 sm:px-6 sm:py-2 text-xs sm:text-sm bg-white/50 backdrop-blur-sm text-gray-600 rounded-full hover:bg-white/70 active:bg-white/80 transition-all duration-300 touch-manipulation"
                >
                  Click if mic doesn't work
                </button>
              )}
            </div>
          )}

          {/* Step 6: Final Message */}
          {step === 6 && (
            <div className="text-center max-w-3xl w-full animate-fade-in px-4">
              <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-10 md:p-16 rounded-2xl sm:rounded-3xl shadow-2xl">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-6 sm:mb-8">
                  🎂 HB, sunshine! 🎂
                </h2>
                <div className="space-y-3 sm:space-y-4 text-base sm:text-lg md:text-xl text-gray-700 font-light leading-relaxed">
                  <p>Wishing you an amazing year ahead,</p>
                  <p>filled with fun and adventures 🎉</p>
                  <p>and many wonderful moments to treasure! ✨</p>
                  {/* <p className="mt-6 sm:mt-8 text-gray-600 italic">
                    Thank you for being such a great friend! 💙
                  </p> */}
                  <p className="text-2xl sm:text-3xl mt-4 sm:mt-6">🎁🎈🎉🌟</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BirthdayExperience;
